import axios from "../utils/index";

// Leading slash so axios joins cleanly against baseURL without a trailing slash.
const API_BASE = "/admin/v1/hospital/analytics";

// On failure, prefer the axios error's `response` (which carries the backend
// `{ code, message }` body) so callers inspecting `res.data.code` see a real
// failure code instead of an opaque Error object. Falls back to a normalized
// shape for network errors that have no response.
const toErrorResult = (err: any) =>
  err?.response ?? {
    data: { code: -1, message: err?.message || "Request failed" },
    status: 0,
  };

const api = {
  // Get notifications - supports both single hospital and network-wide queries
  // POST /admin/v1/hospital/analytics/notifications
  // Body: { hospitalIds: [] }
  getNotifications({
    hospitalIds = [],
    importantOnly = false,
    accountId,
    callback,
  }: {
    hospitalIds?: string[];
    importantOnly?: boolean;
    accountId?: string;
    callback?: (res: any) => void;
  }): Promise<any> {
    return axios
      .post(
        `${API_BASE}/notifications`,
        { hospitalIds, importantOnly },
        { headers: accountId ? { accountId } : {} },
      )
      .then((res) => {
        callback?.(res);
        return res;
      })
      .catch((err) => {
        const result = toErrorResult(err);
        callback?.(result);
        return result;
      });
  },

  // Mark single notification as read
  // PUT /admin/v1/hospital/analytics/notifications/{notificationId}/read
  markAsRead({
    notificationId,
    accountId,
    callback = (() => {}) as any,
  }: {
    notificationId: string;
    accountId?: string;
    callback?: (res: any) => void;
  }): Promise<any> {
    return axios
      .put(
        `${API_BASE}/notifications/${notificationId}/read`,
        {},
        { headers: accountId ? { accountId } : {} },
      )
      .then((res) => {
        callback(res);
        return res;
      })
      .catch((err) => {
        const result = toErrorResult(err);
        callback(result);
        return result;
      });
  },

  // Mark all notifications as read for given hospitals
  // PUT /admin/v1/hospital/analytics/notifications/read-all
  // Body: { hospitalIds: [] }
  markAllAsRead({
    hospitalIds = [],
    accountId,
    callback = (() => {}) as any,
  }: {
    hospitalIds?: string[];
    accountId?: string;
    callback?: (res: any) => void;
  }): Promise<any> {
    return axios
      .put(
        `${API_BASE}/notifications/read-all`,
        { hospitalIds },
        { headers: accountId ? { accountId } : {} },
      )
      .then((res) => {
        callback(res);
        return res;
      })
      .catch((err) => {
        const result = toErrorResult(err);
        callback(result);
        return result;
      });
  },

  // Get unread notification count
  // POST /admin/v1/hospital/analytics/notifications/unread-count
  // Body: { hospitalIds: [] }
  getUnreadCount({
    hospitalIds = [],
    importantOnly = true,
    accountId,
    callback = (() => {}) as any,
  }: {
    hospitalIds?: string[];
    importantOnly?: boolean;
    accountId?: string;
    callback?: (res: any) => void;
  }): Promise<any> {
    return axios
      .post(
        `${API_BASE}/notifications/unread-count`,
        { hospitalIds, importantOnly },
        { headers: accountId ? { accountId } : {} },
      )
      .then((res) => {
        callback(res);
        return res;
      })
      .catch((err) => {
        const result = toErrorResult(err);
        callback(result);
        return result;
      });
  },

  // Register push notification token
  registerPushToken({
    token,
    accountId,
    callback = (() => {}) as any,
  }: {
    token: string;
    accountId?: string;
    callback?: (res: any) => void;
  }) {
    // Attempt standard hospital notification endpoint instead of admin analytics
    axios
      .post(
        `/v1/hospital/notification/register-token`,
        { token },
        { headers: accountId ? { accountId } : {} },
      )
      .then((res) => {
        callback(res);
      })
      .catch((err) => {
        // Fallback: Try 'account' path if 'notification' fails (common alternative)
        console.warn(
          "Register token failed on notification path, trying account path...",
        );
        axios
          .post(
            `/v1/hospital/account/register-token`,
            { token },
            { headers: accountId ? { accountId } : {} },
          )
          .then(callback)
          .catch(callback);
      });
  },
};

export default api;
